'use client'

/**
 * Utility to extract multi-chain addresses from AppKit/WalletConnect sessions.
 * 
 * AppKit (v1.8.19+) stores connection data under @appkit/ localStorage keys:
 * - @appkit/connections — contains all connection data including multi-chain accounts
 * - @appkit/connected_namespaces — which namespaces are active (e.g. "eip155,solana")
 * - @appkit/active_namespace — current active namespace
 * 
 * Trust Wallet returns accounts for all supported chains (eip155, solana, tron, etc.)
 * via WalletConnect v2 session. AppKit stores these in its own format.
 */

export interface WcSessionAccounts {
    eip155: string[]   // EVM addresses (0x...)
    solana: string[]   // Solana addresses (base58)
    tron: string[]     // Tron addresses (T... or 0x...)
    ton: string[]      // TON addresses
}

/**
 * Parse CAIP-10 account string into { namespace, chainId, address }
 * Format: "namespace:chainId:address"
 */
function parseCaipAccount(caipAccount: string): { namespace: string; chainId: string; address: string } | null {
    const parts = caipAccount.split(':')
    if (parts.length < 3) return null
    return {
        namespace: parts[0],
        chainId: parts[1],
        address: parts.slice(2).join(':'),
    }
}

/**
 * Recursively search any value for CAIP-10 account strings.
 */
function findCaipAccounts(value: unknown, found: Set<string>): void {
    if (typeof value === 'string') {
        // Match CAIP-10 pattern: namespace:chainId:address (at least 3 parts separated by colons)
        const caipPattern = /\b(eip155|solana|tron|ton):[A-Za-z0-9_-]+:[A-Za-z0-9]+/g
        const matches = value.match(caipPattern)
        if (matches) matches.forEach(m => found.add(m))
    } else if (Array.isArray(value)) {
        value.forEach(v => findCaipAccounts(v, found))
    } else if (value && typeof value === 'object') {
        for (const v of Object.values(value)) {
            findCaipAccounts(v, found)
        }
    }
}

/**
 * Read AppKit connection data from localStorage and extract multi-chain accounts.
 */
export function getWcSessionAccounts(): WcSessionAccounts {
    const result: WcSessionAccounts = {
        eip155: [],
        solana: [],
        tron: [],
        ton: [],
    }

    if (typeof window === 'undefined') return result

    try {
        const foundAccounts = new Set<string>()

        // Read ALL @appkit/ keys — this is where connections are stored in v1.8.19+
        const keys = Object.keys(localStorage || {})
        for (const key of keys) {
            if (!key.startsWith('@appkit/')) continue

            try {
                const raw = localStorage.getItem(key)
                if (!raw) continue

                // Try JSON parse first, then search raw string
                try {
                    const parsed = JSON.parse(raw)
                    findCaipAccounts(parsed, foundAccounts)
                } catch {
                    findCaipAccounts(raw, foundAccounts)
                }
            } catch { /* skip */ }
        }

        // Also check any wc@2 keys (older WC SDK format, just in case)
        for (const key of keys) {
            if (!key.includes('wc@2')) continue
            try {
                const raw = localStorage.getItem(key)
                if (!raw) continue
                try {
                    findCaipAccounts(JSON.parse(raw), foundAccounts)
                } catch {
                    findCaipAccounts(raw, foundAccounts)
                }
            } catch { /* skip */ }
        }

        // Parse found CAIP accounts into namespace buckets
        for (const account of foundAccounts) {
            const parsed = parseCaipAccount(account)
            if (!parsed) continue
            const key = parsed.namespace as keyof WcSessionAccounts
            if (key in result && !result[key].includes(parsed.address)) {
                result[key].push(parsed.address)
            }
        }

        console.log('[WC Session] Extracted accounts:', {
            eip155: result.eip155,
            solana: result.solana,
            tron: result.tron,
            ton: result.ton,
            totalCaipAccounts: foundAccounts.size,
            rawCaipAccounts: Array.from(foundAccounts),
        })
    } catch (e) {
        console.error('[WC Session] Error extracting accounts:', e)
    }

    return result
}
